import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { i18n } from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import PermissionChecker from 'src/modules/auth/permissionChecker';
import actions from 'src/modules/layout/layoutActions';
import layoutSelectors from 'src/modules/layout/layoutSelectors';

interface MenuItemChild {
  path: string;
  label: string;
  permissionRequired?: any;
  backgroundColor?: string | null;
  color?: string | null;
}

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  exact?: boolean;
  permissionRequired?: any;
  backgroundColor?: string | null;
  color?: string | null;
  count?: number;
  children?: MenuItemChild[];
}

interface MenuProps {
  url: string;
  menus: MenuItem[];
}

const AppMenu = ({ url, menus }: MenuProps) => {
  const dispatch = useDispatch();
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({});

  const currentTenant = useSelector(
    authSelectors.selectCurrentTenant,
  );
  const currentUser = useSelector(
    authSelectors.selectCurrentUser,
  );
  const menuVisible = useSelector(
    layoutSelectors.selectMenuVisible,
  );
  const logoUrl = useSelector(authSelectors.selectLogoUrl);

  const permissionChecker = new PermissionChecker(
    currentTenant,
    currentUser,
  );

  const toggleMenuOnResize = () => {
    window.innerWidth < 576 ? hideMenu() : showMenu();
  };

  useEffect(() => {
    toggleMenuOnResize();
    window.addEventListener('resize', toggleMenuOnResize);

    // Pre-expand the section for the current URL
    const initialExpandedSections = {};
    menus.forEach((menu) => {
      const menuChildren = menu.children || [];
      if (
        menuChildren.length > 0 &&
        (url === menu.path ||
          url.startsWith(menu.path + '/'))
      ) {
        initialExpandedSections[menu.path] = true;
      } else if (menuChildren.length > 0) {
        menuChildren.forEach((child) => {
          if (
            url === child.path ||
            url.startsWith(child.path + '/')
          ) {
            initialExpandedSections[menu.path] = true;
          }
        });
      }
    });
    setExpandedSections(initialExpandedSections);

    return () => {
      window.removeEventListener(
        'resize',
        toggleMenuOnResize,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSelected = (path) => {
    if (url === path) return true;
    if (url.startsWith(path + '/')) return true;
    return false;
  };

  const hideMenu = () => {
    dispatch(actions.doHideMenu());
  };

  const showMenu = () => {
    dispatch(actions.doShowMenu());
  };

  const match = (permission) => {
    return permissionChecker.match(permission);
  };

  const lockedForCurrentPlan = (permission) => {
    return permissionChecker.lockedForCurrentPlan(
      permission,
    );
  };

  const toggleSection = (path) => {
    setExpandedSections((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  return (
    <TooltipProvider delayDuration={300}>
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 border-r bg-background transition-all duration-300',
          menuVisible ? 'w-64' : 'w-16',
        )}
      >
        {/* Header with logo and company name */}
        <div
          className={cn(
            'flex h-16 items-center border-b',
            menuVisible ? 'px-4' : 'px-2',
          )}
        >
          <Link
            to="/"
            className={cn(
              'flex items-center w-full overflow-hidden',
              menuVisible ? 'space-x-2' : 'justify-center',
            )}
          >
            <img
              src={logoUrl}
              alt="Logo"
              className="h-10 w-10 rounded-md shrink-0 object-contain"
            />
            <div
              className={cn(
                'flex flex-col flex-grow',
                !menuVisible && 'hidden',
              )}
            >
              <span className="font-semibold text-sm truncate">
                {currentTenant.name}
              </span>
              <span className="text-sm text-muted-foreground truncate">
                {i18n('common.company')}
              </span>
            </div>
            {menuVisible && (
              <div className="ml-auto">
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
          </Link>
        </div>

        <ScrollArea className="h-[calc(100vh-8rem)]">
          <nav className="p-2 space-y-1">
            {menus.map((menu) => {
              const hasPermission =
                !menu.permissionRequired ||
                match(menu.permissionRequired);
              const isLocked =
                menu.permissionRequired &&
                lockedForCurrentPlan(
                  menu.permissionRequired,
                );

              if (!hasPermission) {
                return null;
              }

              const isExpanded =
                expandedSections[menu.path];
              const menuChildren = menu.children || [];
              const hasChildren = menuChildren.length > 0;

              const isActive =
                isSelected(menu.path) ||
                (hasChildren &&
                  menuChildren.some((child) =>
                    isSelected(child.path),
                  ));

              // When menu is collapsed, we only show icons with tooltips
              if (!menuVisible) {
                return (
                  <div key={menu.path} className="mb-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={
                            isActive ? 'secondary' : 'ghost'
                          }
                          size="icon"
                          className={cn(
                            'w-full h-10 flex items-center justify-center',
                            isActive && 'bg-accent',
                            isLocked &&
                              'opacity-50 cursor-not-allowed',
                          )}
                          asChild={!hasChildren}
                          disabled={isLocked}
                          onClick={
                            hasChildren
                              ? () =>
                                  toggleSection(menu.path)
                              : undefined
                          }
                        >
                          {hasChildren ? (
                            <div className="flex items-center justify-center">
                              <div className="h-5 w-5">
                                {menu.icon}
                              </div>
                            </div>
                          ) : (
                            <Link
                              to={menu.path}
                              className="flex items-center justify-center"
                            >
                              <div className="h-5 w-5">
                                {menu.icon}
                              </div>
                            </Link>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="right"
                        className="z-50 font-medium"
                      >
                        <p>{i18n(menu.label)}</p>
                        {menu.count !== undefined && (
                          <p className="text-xs text-muted-foreground text-center">
                            {menu.count}
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>

                    {/* Even when collapsed, show submenu if expanded */}
                    {hasChildren && isExpanded && (
                      <div className="absolute left-16 mt-0 bg-popover rounded-md border shadow-md min-w-40 z-50">
                        {menuChildren.map((child) => {
                          const childHasPermission =
                            !child.permissionRequired ||
                            match(child.permissionRequired);
                          const childIsLocked =
                            child.permissionRequired &&
                            lockedForCurrentPlan(
                              child.permissionRequired,
                            );

                          if (!childHasPermission) {
                            return null;
                          }

                          const childIsActive = isSelected(
                            child.path,
                          );

                          return (
                            <Button
                              key={child.path}
                              variant={
                                childIsActive
                                  ? 'secondary'
                                  : 'ghost'
                              }
                              className={cn(
                                'w-full justify-start h-8 text-sm rounded-none',
                                childIsLocked &&
                                  'opacity-50 cursor-not-allowed',
                              )}
                              asChild
                              disabled={childIsLocked}
                            >
                              <Link to={child.path}>
                                <span>
                                  {i18n(child.label)}
                                </span>
                              </Link>
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Normal expanded menu view
              return (
                <div key={menu.path} className="space-y-1">
                  {/* Main menu item */}
                  <div className="flex flex-col">
                    {hasChildren ? (
                      <button
                        onClick={() =>
                          toggleSection(menu.path)
                        }
                        className={cn(
                          'flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium',
                          isActive
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent/50',
                          isLocked &&
                            'opacity-50 cursor-not-allowed',
                        )}
                        disabled={isLocked}
                      >
                        <div className="flex items-center gap-2">
                          {menu.icon}
                          <span className="text-sm">
                            {i18n(menu.label)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {menu.count !== undefined && (
                            <span className="text-xs text-muted-foreground">
                              {menu.count}
                            </span>
                          )}
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                      </button>
                    ) : (
                      <Button
                        variant={
                          isActive ? 'secondary' : 'ghost'
                        }
                        className={cn(
                          'w-full justify-start gap-2',
                          isLocked &&
                            'opacity-50 cursor-not-allowed',
                        )}
                        asChild
                        disabled={isLocked}
                      >
                        <Link to={menu.path}>
                          {menu.icon}
                          <span>{i18n(menu.label)}</span>
                          {menu.count !== undefined && (
                            <span className="ml-auto text-xs text-muted-foreground">
                              {menu.count}
                            </span>
                          )}
                        </Link>
                      </Button>
                    )}
                  </div>

                  {/* Submenu items */}
                  {hasChildren && isExpanded && (
                    <div className="ml-6 space-y-1 mt-1">
                      {menuChildren.map((child) => {
                        const childHasPermission =
                          !child.permissionRequired ||
                          match(child.permissionRequired);
                        const childIsLocked =
                          child.permissionRequired &&
                          lockedForCurrentPlan(
                            child.permissionRequired,
                          );

                        if (!childHasPermission) {
                          return null;
                        }

                        const childIsActive = isSelected(
                          child.path,
                        );

                        return (
                          <Button
                            key={child.path}
                            variant={
                              childIsActive
                                ? 'secondary'
                                : 'ghost'
                            }
                            className={cn(
                              'w-full justify-start h-8 text-sm',
                              childIsLocked &&
                                'opacity-50 cursor-not-allowed',
                            )}
                            asChild
                            disabled={childIsLocked}
                          >
                            <Link to={child.path}>
                              <span>
                                {i18n(child.label)}
                              </span>
                            </Link>
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User profile at bottom */}
        {currentUser && (
          <div className="absolute bottom-0 left-0 right-0 border-t p-4">
            {menuVisible ? (
              // Versión expandida del perfil
              <div className="flex items-center">
                {currentUser.avatars &&
                currentUser.avatars[0] ? (
                  <img
                    src={currentUser.avatars[0].downloadUrl}
                    alt={currentUser.fullName}
                    className="h-8 w-8 rounded-full shrink-0"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted shrink-0">
                    <span className="text-xs font-medium">
                      {currentUser.fullName?.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="flex flex-col flex-grow pl-2 overflow-hidden">
                  <span className="font-medium text-sm truncate">
                    {currentUser.fullName}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {currentUser.email}
                  </span>
                </div>
                <div className="ml-auto">
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                </div>
              </div>
            ) : (
              // Versión colapsada del perfil con tooltip
              <Tooltip>
                <TooltipTrigger className="w-full flex justify-center">
                  {currentUser.avatars &&
                  currentUser.avatars[0] ? (
                    <img
                      src={
                        currentUser.avatars[0].downloadUrl
                      }
                      alt={currentUser.fullName}
                      className="h-8 w-8 rounded-full"
                    />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                      <span className="text-xs font-medium">
                        {currentUser.fullName?.charAt(0)}
                      </span>
                    </div>
                  )}
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="z-50"
                >
                  <div className="py-1">
                    <p className="font-medium">
                      {currentUser.fullName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentUser.email}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
};

export default AppMenu;
